import { Alert, Input, Select, Table } from "antd";
import React, { useState } from "react";
import { BiSearch } from "react-icons/bi";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { selectUser } from "../../redux/slices/auth.slice";
import useGroupBranchSwitch from "./useGroupBranchSwitch";

const STAT_COLORS = {
  indigo: { bg: "#eef2ff", text: "#4f46e5" },
  sky: { bg: "#f0f9ff", text: "#0284c7" },
  blue: { bg: "#eff6ff", text: "#2563eb" },
  rose: { bg: "#fff1f2", text: "#e11d48" },
  purple: { bg: "#faf5ff", text: "#9333ea" },
  emerald: { bg: "#ecfdf5", text: "#059669" },
  amber: { bg: "#fffbeb", text: "#b45309" },
};


const GroupListScreen = ({
  title,
  queryKey,
  fetchFn,
  columns,
  branchOptions = [],
  branchOptionsLoading = false,
  searchPlaceholder = "Search…",
  extraHeader = null,
  statCards = null,
  pageSize = 20,
}) => {
  const user = useSelector(selectUser);
  const groupId = user?.group;
  const { goToBranch } = useGroupBranchSwitch();

  const [search, setSearch] = useState("");
  const [branchFilter, setBranchFilter] = useState(undefined);

  const params = { search: search || undefined, school: branchFilter };
  const result = useQuery(
    [queryKey, groupId, params],
    () => fetchFn(groupId, params),
    { enabled: !!groupId },
  );

  const branchColumn = {
    title: "Branch",
    key: "branch",
    width: 160,
    render: (r) => (
      <span
        className="text-primary-600 cursor-pointer hover:underline"
        onClick={() => goToBranch(r.school)}
        title="Switch to this branch"
      >
        {r.school_name}
      </span>
    ),
  };

  const rows = result.data?.data || [];
  const cards = statCards ? statCards(rows) : null;

  return (
    <div className="p-8">
      {!!cards?.length && (
        <div
          className="grid grid-cols-4 gap-3"
          style={{ marginBottom: 40 }}
        >
          {cards.map((c) => {
            const palette = STAT_COLORS[c.color] || STAT_COLORS.indigo;
            return (
              <div
                key={c.key ?? c.label}
                className="bg-white rounded-xl p-3 shadow-sm border border-slate-100 hover:shadow-md transition-all duration-300"
                style={{ borderLeft: `3px solid ${palette.text}` }}
              >
                <div className="flex justify-between items-center mb-2">
                  <span className="text-slate-400 text-[10px] font-semibold uppercase tracking-wider truncate">
                    {c.label}
                  </span>
                  {c.icon && (
                    <div
                      className="p-1 rounded-md shrink-0"
                      style={{ backgroundColor: palette.bg, color: palette.text }}
                    >
                      {c.icon}
                    </div>
                  )}
                </div>
                <h3 className="text-lg font-bold text-slate-800">
                  {result.isLoading ? "—" : c.value}
                </h3>
                {c.sub && (
                  <p className="text-slate-400 text-[10px] mt-1 truncate">{c.sub}</p>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div
        style={{ borderRadius: "10px", color: "#616161" }}
        className="bg-white drop-shadow-md p-8 w-full"
      >
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="text-2xl text-orange-500 font-semibold">
            {title}
          </div>
          <div className="flex flex-row items-center gap-4">
            {extraHeader}
            <Select
              allowClear
              placeholder="All branches"
              style={{ width: 180 }}
              options={branchOptions}
              value={branchFilter}
              onChange={setBranchFilter}
              loading={branchOptionsLoading}
            />
            <Input.Search
              value={search}
              placeholder={searchPlaceholder}
              enterButton="Search"
              style={{ backgroundColor: "#E3E3E3" }}
              prefix={<BiSearch className="text-gray-600" />}
              onSearch={setSearch}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {!!result.data?.errors?.length && (
          <Alert
            className="mt-4"
            type="warning"
            showIcon
            message={`Could not load ${result.data.errors.length} branch${
              result.data.errors.length > 1 ? "es" : ""
            }: ${result.data.errors.map((e) => e.school_name).join(", ")}`}
          />
        )}

        <div className="py-5">
          <Table
            rowKey={(r) => `${r.school}-${r._id}`}
            columns={[branchColumn, ...columns]}
            dataSource={rows}
            loading={result.isLoading}
            pagination={{ pageSize }}
          />
        </div>
      </div>
    </div>
  );
};

export default GroupListScreen;
